import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { format } from 'date-fns';
import { ClipboardList, GitCommit } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import type { Commit, TimeEntry } from '@/pages/developer/types';

export type AssignCommitsToGitOrderDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  commits: Commit[];
  commitShas: string[];
  gitOrders: TimeEntry[];

  selectedGitOrderId: string;
  setSelectedGitOrderId: Dispatch<SetStateAction<string>>;

  newGitOrderDescription: string;
  setNewGitOrderDescription: Dispatch<SetStateAction<string>>;
  onCreateGitOrder: () => Promise<void>;
  creating: boolean;

  onAssign: () => Promise<void>;
  assigning: boolean;
};

export function AssignCommitsToGitOrderDialog({
  open,
  onOpenChange,
  commits,
  commitShas,
  gitOrders,
  selectedGitOrderId,
  setSelectedGitOrderId,
  newGitOrderDescription,
  setNewGitOrderDescription,
  onCreateGitOrder,
  creating,
  onAssign,
  assigning,
}: AssignCommitsToGitOrderDialogProps) {
  const [gitOrderSearch, setGitOrderSearch] = useState('');

  useEffect(() => {
    if (open) setGitOrderSearch('');
  }, [open]);

  const hasGitOrders = gitOrders.length > 0;
  const isBusy = assigning || creating;
  const selectedCommits = commitShas
    .map((sha) => commits.find((candidate) => candidate.sha === sha) ?? null)
    .filter((value): value is Commit => Boolean(value));
  const primaryCommit = selectedCommits[0] ?? null;
  const commitCount = selectedCommits.length;
  const isBulk = commitCount > 1;

  const filteredGitOrders = useMemo(() => {
    const query = gitOrderSearch.trim().toLowerCase();
    if (!query) return gitOrders;
    return gitOrders.filter((entry) => {
      const date = new Date(entry.date);
      const labelDate = Number.isNaN(date.getTime()) ? '' : format(date, 'MMM d, yyyy');
      const description = (entry.description ?? '').toLowerCase();
      return (
        description.includes(query) ||
        labelDate.toLowerCase().includes(query) ||
        entry.id.toLowerCase().includes(query) ||
        entry.date.toLowerCase().includes(query)
      );
    });
  }, [gitOrderSearch, gitOrders]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            Assign commits to Git order
          </DialogTitle>
          <DialogDescription>
            Select a Git order, then assign {isBulk ? `${commitCount} commits` : 'this commit'}.
          </DialogDescription>
        </DialogHeader>

        {commitCount === 0 ? (
          <div className="rounded-md border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">No commits selected.</div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-border/60 bg-muted/10 p-3">
              <div className="flex items-center gap-2 text-xs font-mono" title={primaryCommit?.sha ?? ''}>
                <GitCommit className="h-4 w-4 text-muted-foreground" />
                {isBulk ? `${commitCount} commits` : primaryCommit?.sha.slice(0, 7)}
              </div>
              {primaryCommit && (
                <p className="mt-1 text-xs text-muted-foreground line-clamp-2" title={primaryCommit.message}>
                  {primaryCommit.message}
                </p>
              )}
            </div>

            {hasGitOrders ? (
              <div className="space-y-2">
                <Input
                  aria-label="Search Git orders"
                  placeholder="Search Git orders…"
                  value={gitOrderSearch}
                  onChange={(event) => setGitOrderSearch(event.target.value)}
                />

                {filteredGitOrders.length === 0 ? (
                  <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm text-muted-foreground">
                    No matching Git orders.
                  </div>
                ) : (
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={selectedGitOrderId}
                    onChange={(event) => setSelectedGitOrderId(event.target.value)}
                  >
                    <option value="">Select a Git order…</option>
                    {filteredGitOrders.map((entry) => {
                      const date = new Date(entry.date);
                      const labelDate = Number.isNaN(date.getTime()) ? '—' : format(date, 'MMM d, yyyy');
                      return (
                        <option key={entry.id} value={entry.id}>
                          {labelDate} — {entry.description}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                No Git orders yet.
              </div>
            )}

            <div className="rounded-md border border-border/60 bg-muted/10 p-3 space-y-2">
              <p className="text-xs text-muted-foreground">Or create a new Git order for this commit day.</p>
              <Input
                placeholder="New Git order description"
                value={newGitOrderDescription}
                onChange={(event) => setNewGitOrderDescription(event.target.value)}
              />
              <div className="flex justify-end">
                <Button type="button" variant="outline" onClick={onCreateGitOrder} disabled={isBusy || !newGitOrderDescription.trim()}>
                  {creating ? 'Creating…' : 'Create Git order'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
            Cancel
          </Button>
          <Button type="button" onClick={onAssign} disabled={commitCount === 0 || !selectedGitOrderId || isBusy}>
            {assigning ? 'Assigning…' : isBulk ? 'Assign' : 'Select'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Backwards-compatible export (internal usage only).
export const AssignCommitToWorkorderDialog = AssignCommitsToGitOrderDialog;
